import { Stack } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { EnergyAnalysisRow } from "../model/types";

import {
  buildCombinationChangeBarOption,
  buildCombinationTrendHeatmapOption,
  buildMixChangeMatrixOption,
  buildMixMosaicOption,
} from "../lib/buildMixInsightOptions";
import { collectYears } from "../lib/aggregate";
import { EnergyAnalysisInsightChart } from "./EnergyAnalysisInsightChart";

type EnergyAnalysisMixInsightsProps = {
  rows: EnergyAnalysisRow[];
  snapshotYear?: number;
  unit: string;
};

export const EnergyAnalysisMixInsights = ({
  rows,
  snapshotYear,
  unit,
}: EnergyAnalysisMixInsightsProps) => {
  const { t } = useTranslation();
  const years = useMemo(() => collectYears(rows), [rows]);
  const startYear = years[0];
  const endYear = years[years.length - 1];
  const mosaicYear = snapshotYear ?? endYear;
  const trendOption = useMemo(() => buildCombinationTrendHeatmapOption(rows, unit), [rows, unit]);
  const changeOption = useMemo(() => buildCombinationChangeBarOption(rows, unit), [rows, unit]);
  const matrixOption = useMemo(() => buildMixChangeMatrixOption(rows, unit), [rows, unit]);
  const mosaicOption = useMemo(
    () => buildMixMosaicOption(rows, unit, snapshotYear),
    [rows, snapshotYear, unit]
  );
  return (
    <Stack className="w-full flex-col items-stretch gap-4">
      <div className="grid w-full grid-cols-2 items-stretch gap-4">
        <EnergyAnalysisInsightChart
          height="30rem"
          iconName="GridIcon"
          option={trendOption}
          title={`${t("BuildingProfile.energyAnalysis.mixInsights.combinationTrend")} (${unit})`}
        />
        <EnergyAnalysisInsightChart
          height="30rem"
          iconName="Barchart"
          option={changeOption}
          title={t("BuildingProfile.energyAnalysis.mixInsights.changeByEnd", { year: endYear })}
        />
      </div>
      <div className="grid w-full grid-cols-2 items-stretch gap-4">
        <EnergyAnalysisInsightChart
          height="30rem"
          iconName="GridIcon"
          option={matrixOption}
          title={t("BuildingProfile.energyAnalysis.mixInsights.changeMatrix", {
            endYear,
            startYear,
          })}
        />
        <EnergyAnalysisInsightChart
          height="30rem"
          option={mosaicOption}
          title={t("BuildingProfile.energyAnalysis.mixInsights.mosaic", { year: mosaicYear })}
        />
      </div>
    </Stack>
  );
};
